import * as THREE from 'three';

const DIM_CAP = 0.7;

interface RoomSlot {
  total: number;
  dead: number;
}

/**
 * Lerps a room's baked vertex colors toward the fill-only bake as fluorescents break.
 * Never goes past the OFF bake (ambient + unbreakable fills).
 */
export class RoomLampDim {
  private readonly rooms = new Map<string, RoomSlot>();
  private readonly meshes: THREE.Mesh[] = [];
  private readonly instanced: THREE.InstancedMesh[] = [];

  constructor(root: THREE.Object3D) {
    root.traverse((o) => {
      if (o.userData.destructible === 'lamp' && !o.userData.alarmBeacon) {
        const id = (o.userData.bakeRoomId as string) || '';
        if (!id) return;
        const slot = this.rooms.get(id) ?? { total: 0, dead: 0 };
        slot.total += 1;
        this.rooms.set(id, slot);
      }
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh) return;
      if ((mesh as THREE.InstancedMesh).isInstancedMesh && mesh.userData.bakeRoomIds) {
        this.instanced.push(mesh as THREE.InstancedMesh);
        return;
      }
      if (mesh.geometry?.userData.colorOn && mesh.userData.bakeRoomId) {
        this.meshes.push(mesh);
      }
    });
  }

  noteBreak(lamp: THREE.Object3D): void {
    if (lamp.userData.alarmBeacon) return;
    const id = (lamp.userData.bakeRoomId as string) || '';
    const slot = this.rooms.get(id);
    if (!slot || slot.total <= 0) return;
    slot.dead = Math.min(slot.total, slot.dead + 1);
    const t = Math.min(1, slot.dead / slot.total) * DIM_CAP;
    this.applyRoom(id, t);
  }

  private applyRoom(roomId: string, t: number): void {
    for (const mesh of this.meshes) {
      if (mesh.userData.bakeRoomId !== roomId) continue;
      const geo = mesh.geometry;
      const on = geo.userData.colorOn as Float32Array | undefined;
      const off = geo.userData.colorOff as Float32Array | undefined;
      const attr = geo.attributes.color;
      if (!on || !off || !attr) continue;
      mixInto(on, off, attr.array as Float32Array, t);
      attr.needsUpdate = true;
    }
    for (const mesh of this.instanced) {
      const ids = mesh.userData.bakeRoomIds as string[] | undefined;
      const on = mesh.userData.colorOn as Float32Array | undefined;
      const off = mesh.userData.colorOff as Float32Array | undefined;
      const attr = mesh.instanceColor;
      if (!ids || !on || !off || !attr) continue;
      const out = attr.array as Float32Array;
      let touched = false;
      for (let i = 0; i < ids.length; i++) {
        if (ids[i] !== roomId) continue;
        const o = i * 3;
        out[o] = on[o]! + (off[o]! - on[o]!) * t;
        out[o + 1] = on[o + 1]! + (off[o + 1]! - on[o + 1]!) * t;
        out[o + 2] = on[o + 2]! + (off[o + 2]! - on[o + 2]!) * t;
        touched = true;
      }
      if (touched) attr.needsUpdate = true;
    }
  }
}

function mixInto(on: Float32Array, off: Float32Array, out: Float32Array, t: number): void {
  const n = Math.min(on.length, off.length, out.length);
  for (let i = 0; i < n; i++) {
    out[i] = on[i]! + (off[i]! - on[i]!) * t;
  }
}
